import {
  checkboxClasses,
  formControlLabelClasses,
  typographyClasses,
} from '@mui/material';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { OrderUnitOfMeasure } from '@treadinc/horizon-api-spec';
import { Dayjs } from 'dayjs';
import { t } from 'i18next';
import { isNil } from 'lodash-es';
import { useFormContext } from 'react-hook-form';

import { AutocompleteFormField } from '~components/FormFields/AutocompleteFormField';
import { CheckBoxFormField } from '~components/FormFields/CheckBoxFormField';
import NumberInput from '~components/FormFields/v2/NumberInput';
import { NameIdSchemaRequired } from '~constants/regexConst';
import { useAllowedUnitOfMeasureOptions } from '~hooks/useAllowedUnitOfMeasureOptions';

type JobDetailsV2Dto = {
  jobStartAt: Dayjs | null;
  material: NameIdSchemaRequired | null;
  quantity: number | null;
  unitOfMeasure: NameIdSchemaRequired | null;
  recurringLoads: boolean;
  requestedLoadsCount: number | null;
  equipmentTypeGrossCapacity: number | null;
  loadUnitOfMeasure: NameIdSchemaRequired | null;
};

interface JobDetailsV2Props {
  userOwnsJob?: boolean;
}

export default function JobDetailsV2({
  children,
  userOwnsJob = true,
}: React.PropsWithChildren<JobDetailsV2Props>) {
  const form = useFormContext<JobDetailsV2Dto>();
  const unitOfMeasureOptions = useAllowedUnitOfMeasureOptions();
  const watchedUnitOfMeasure = form.watch('unitOfMeasure');

  return (
    <Card sx={{ mb: 2, p: 2 }}>
      <Typography variant="form-section-title" sx={{ mb: 1 }}>
        {t('form_fields.truck')}
      </Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 6 }}>
          <AutocompleteFormField
            clearable
            control={form.control}
            disabled={!userOwnsJob}
            errors={form.formState.errors}
            getLabel={(item) => item?.name || ''}
            getValue={(item) => item?.id || ''}
            label={t('form_fields.units')}
            list={unitOfMeasureOptions}
            name="unitOfMeasure"
            dataUi={`page-dispatch__job-details-v2__unit-of-measure-input`}
          />
        </Grid>

        <Grid size={{ xs: 4 }}>
          <NumberInput
            control={form.control}
            isDisabled={!userOwnsJob}
            isRequired={
              !isNil(watchedUnitOfMeasure) &&
              watchedUnitOfMeasure?.id !== OrderUnitOfMeasure.LOAD
            }
            label={t('form_fields.quantity')}
            name="quantity"
          />
        </Grid>

        <Grid size={{ xs: 2 }}>
          <CheckBoxFormField
            control={form.control}
            disabled={!userOwnsJob}
            errors={form.formState.errors}
            label={`${t('common.running')}`}
            name="recurringLoads"
            value={Boolean(form.getValues('recurringLoads'))}
            sx={{
              mt: '14px',
              [`& .${formControlLabelClasses.root}`]: { mr: 0 },
              [`& .${checkboxClasses.root}`]: { pr: '2px' },
              [`& .${typographyClasses.root}`]: { fontSize: '12px', fontWeight: 400 },
            }}
            dataUi={`page-dispatch__job-details-v2__recurring-loads-input`}
          />
        </Grid>

        {children}
      </Grid>
    </Card>
  );
}
